import * as THREE from "three";
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

import partTexture from '../../assets/img/textures/spark.png';
import pathToModel from '../../assets/models/threeBone.glb';
import texturePath from '../../assets/img/textures/grid.jpg';
import { coloursToArray } from '../../utils';

export default class SceneController {
    constructor(domElem) {
        this.domElem = domElem;
        this.scene = new THREE.Scene();
        this.renderer = new THREE.WebGLRenderer({ antialias: true });
    }

    setup() {
        this.colourArr = (0, coloursToArray)('https://coolors.co/fcecc9-fcb0b3-f93943-7eb2dd-445e93'); //('https://coolors.co/d5573b-885053-777da7-94c9a9-c6ecae');
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.domElem.appendChild(this.renderer.domElement);

        this.disposed = false;
        this.scene.background = this.colourArr[4];
        this.scene.fog = new THREE.Fog(this.colourArr[4], 50, 200);

        this.isCamOrbit = false;

        this.setupCamera();
        this.setupLight();
        this.setupControls();

        this.createSkyboxFromTexture();
        this.loadModels();
        this.setupGlobalUniforms();
    }

    setupCamera() {
        let VIEW_ANGLE = 60;
        let ASPECT_RATIO = window.innerWidth / window.innerHeight;
        let NEAR_PLANE = 0.1;
        let FAR_PLANE = 1000;

        this.camera = new THREE.PerspectiveCamera(VIEW_ANGLE, ASPECT_RATIO, NEAR_PLANE, FAR_PLANE);
        this.camera.position.set(-0.1, 10, 80);
    }

    setupLight() {
        let pointLight = new THREE.PointLight(this.colourArr[4], 0.8);
        pointLight.position.set(2.5, 10, 2.5);
        this.scene.add(pointLight);

        let pointLight2 = new THREE.PointLight(this.colourArr[4], 0.8);
        pointLight2.position.set(-2.5, 0, -2.5);
        this.scene.add(pointLight2);

        let pointLight3 = new THREE.PointLight(this.colourArr[4], 0.8);
        pointLight3.position.set(0.5, -0.2, 0);
        this.scene.add(pointLight3);

        let hemiLight = new THREE.HemisphereLight(this.colourArr[1], this.colourArr[4], 1);
        hemiLight.position.set(0, 0.5, 0);
        this.scene.add(hemiLight);
    }

    setupControls() {
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        if (!this.isCamOrbit) this.controls.autoRotate = true;
        this.controls.autoRotateSpeed = 0.25;
        this.controls.zoomSpeed = 1.2;
        this.controls.panSpeed = 0.8;
        this.controls.enableZoom = true;
        this.controls.enablePan = true;
        this.controls.enableDamping = true;
        this.controls.dampingFactor = 0.3;
        this.controls.maxDistance = 100;
        this.controls.minDistance = 1;
    }

    setupGlobalUniforms() {
        let texLoader = new THREE.TextureLoader();
        let particleMap = texLoader.load(partTexture);
        this.uniforms = {
            uResolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
            uTime: { value: 0 },
            uTexture0: { value: particleMap }
        };
    }

    createSkyboxFromTexture() {
        let skyboxMatArr = [];
        let loader = new THREE.TextureLoader();
        let grid = loader.load(texturePath);

        skyboxMatArr.push(new THREE.MeshBasicMaterial({ color: this.colourArr[0], map: grid }));
        skyboxMatArr.push(new THREE.MeshBasicMaterial({ color: this.colourArr[2], map: grid }));
        skyboxMatArr.push(new THREE.MeshBasicMaterial({ color: this.colourArr[3], map: grid }));
        skyboxMatArr.push(new THREE.MeshBasicMaterial({ color: this.colourArr[4], map: grid }));
        skyboxMatArr.push(new THREE.MeshBasicMaterial({ color: this.colourArr[2], map: grid }));
        skyboxMatArr.push(new THREE.MeshBasicMaterial({ color: this.colourArr[1], map: grid }));

        let skyboxGeo = new THREE.BoxBufferGeometry(1, 1, 1);
        skyboxGeo.scale(100, 100, -100);

        let skyboxMesh = new THREE.Mesh(skyboxGeo, skyboxMatArr);
        this.scene.add(skyboxMesh);
    }

    setupMirrorMaterial() {
        this.mirrorSphereCamera = new THREE.CubeCamera(0.01, 100, 512);
        // mirrorCubeCamera.renderTarget.minFilter = THREE.LinearMipMapLinearFilter;
        this.scene.add(this.mirrorSphereCamera);
        let mirrorSphereMaterial = new THREE.MeshBasicMaterial({ envMap: this.mirrorSphereCamera.renderTarget.texture });
        return mirrorSphereMaterial;
    }

    updateMirrorMaterial() {
        this.mirrorSphereCamera.update(this.renderer, this.scene);
    };

    loadModels() {
        let gltfLoader = new GLTFLoader();
        let material = this.setupMirrorMaterial();
        gltfLoader.load(pathToModel,
            (gltf) => {
                gltf.scene.scale.set(1, 1, 1);
                gltf.scene.position.set(0, 40, 50);
                gltf.scene.traverse(child => {
                    if (child.isMesh) {
                        // child.castShadow = true;
                        // child.receiveShadow = true;
                        // child.material.metalness = 0;
                        child.material = material;
                    }
                });
                this.scene.add(gltf.scene);
            });
        console.log('Loading Complete!');
    }

    orbitCamera(time) {
        //glitch for Stan's video
        // let x = this.camera.position.x;
        // let z = this.camera.position.z;
        // this.camera.position.x = x * Math.cos(time);
        // this.camera.position.z = z * Math.sin(time);
        // this.camera.lookAt(new THREE.Vector3(0,0,0));

        let factor = 30;
        this.camera.position.x = 20 + factor * Math.cos(time);
        this.camera.position.z = 10 + factor * Math.sin(time);
        this.camera.position.y = 40 + factor * Math.sin(time);

        this.camera.lookAt(new THREE.Vector3(0, 0, 0));
    }

    updateSceneSubjects(time) {
        this.updateMirrorMaterial();
    }

    update() {
        if (this.disposed) return;
        if (this.controls) this.controls.update();
        if (this.isCamOrbit) this.orbitCamera(Date.now() * 0.001);
        this.updateSceneSubjects(Date.now() * 0.005);
        this.renderer.render(this.scene, this.camera);
    }

    onWindowResize() {
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(window.innerWidth, window.innerHeight);
    }

    onKeyDown(e) {
        this.isCamOrbit = true;
    }

    onKeyUp(e) {
        this.isCamOrbit = false;
    }

    dispose() {
        this.disposed = true;
        this.scene.children.forEach(child => {
            if (child.isMesh) {
                // child.material.dispose(); //TODO: fix mistake with this line
                child.geometry.dispose();
            }
        });
        this.scene.dispose();
        this.renderer.dispose();
    }
} 